import { useMemo } from 'react';

import {
  isAdminOfOrganization,
  isAIEnabledForPremOrganization,
} from '@trello/business-logic/organization';

import {
  type AiBoardMembershipQuery,
  useAiBoardMembershipQuery,
} from './AiBoardMembershipQuery.generated';
import { type MemberAdminDataFragment } from './MemberAdminData.generated';

interface UseWorkspaceAIDataProps {
  boardId: string;
  member?: MemberAdminDataFragment;
  skip: boolean;
}

export function useWorkspaceAiData({
  boardId,
  member,
  skip,
}: UseWorkspaceAIDataProps): {
  isAIEnabled: boolean | null | undefined;
  isWorkspaceAdmin: boolean | '' | null | undefined;
  board: AiBoardMembershipQuery['board'];
} {
  // We need the full board & workspace data to determine if the user is a workspace admin
  const { data: membershipData } = useAiBoardMembershipQuery({
    variables: { boardId },
    waitOn: ['CurrentBoardInfo', 'MemberHeader'],
    fetchPolicy: 'cache-only',
    skip: skip || !member,
  });
  const board = membershipData?.board;

  const workspace = membershipData?.board?.organization;
  const isAIEnabled = workspace && isAIEnabledForPremOrganization(workspace);

  const isWorkspaceAdmin = useMemo(() => {
    return member && workspace && isAdminOfOrganization(member, workspace);
  }, [member, workspace]);

  return {
    isAIEnabled,
    isWorkspaceAdmin,
    board,
  };
}
